import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  EnvironmentInjector,
  afterNextRender,
  computed,
  createComponent,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { HeaderComponent } from '@shared/header/header.component';
import { SidebarComponent } from '@shared/sidebar/sidebar.component';
import { ModalService } from '@core/services/modal.service';
import { SidebarService } from '@core/services/sidebar.service';
import { LoadingComponent } from '@shared/loading/loading.component';
import { GlobalLoadingService } from '@core/services/global-loading.service';
import { SystemNotificationBannerComponent } from '@shared/components/system-notification-banner/system-notification-banner.component';
import { AuthService } from '@modules/auth/auth.service';
import type { OnboardingWizardComponent } from '@shared/onboarding/onboarding-wizard.component';

/** Coincide con la duración de la transición del sidebar móvil (0.28s). */
const CONTENT_INERT_DELAY_MS = 280;

@Component({
    selector: 'app-layout',
    imports: [RouterOutlet, HeaderComponent, SidebarComponent, CommonModule, LoadingComponent, SystemNotificationBannerComponent],
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  readonly modalService = inject(ModalService);
  readonly sidebarService = inject(SidebarService);
  readonly globalLoadingService = inject(GlobalLoadingService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private onboardingComponentRef: ComponentRef<OnboardingWizardComponent> | null = null;
  private onboardingHostElement: HTMLElement | null = null;
  private isDestroyed = false;
  private onboardingRemoteCheckInFlight: Promise<boolean> | null = null;
  readonly isMobileViewport = toSignal(this.sidebarService.isMobileViewport$, {
    initialValue: this.sidebarService.isMobileViewport,
  });
  readonly isMobileMenuOpen = toSignal(this.sidebarService.isMobileOpen$, {
    initialValue: this.sidebarService.isMobileOpen,
  });
  readonly isMobileSidebarOpen = computed(
    () => this.isMobileViewport() && this.isMobileMenuOpen(),
  );
  // `inert`/`aria-hidden` recalculan estilos de todo el contenido; se aplican
  // cuando termina la transición del sidebar para no trabar la animación.
  readonly isContentInert = signal(false);
  private contentInertTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const shouldBeInert = this.isMobileSidebarOpen();
      this.clearContentInertTimer();

      if (shouldBeInert === this.isContentInert()) {
        return;
      }

      this.contentInertTimer = setTimeout(() => {
        this.contentInertTimer = null;
        this.isContentInert.set(shouldBeInert);
      }, CONTENT_INERT_DELAY_MS);
    });

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.globalLoadingService.startRouteLoading();
          return;
        }

        if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          this.globalLoadingService.stopRouteLoading();
        }
      });

    this.authService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.hasCompletedOnboarding()) {
          this.destroyOnboarding();
        }
      });

    this.destroyRef.onDestroy(() => {
      this.isDestroyed = true;
      this.clearContentInertTimer();
      this.destroyOnboarding();
      this.globalLoadingService.stopRouteLoading();
    });

    afterNextRender(() => {
      void this.mountOnboardingIfNeeded();
    });
  }

  private clearContentInertTimer(): void {
    if (this.contentInertTimer === null) {
      return;
    }

    clearTimeout(this.contentInertTimer);
    this.contentInertTimer = null;
  }

  private async mountOnboardingIfNeeded(): Promise<void> {
    if (
      this.isDestroyed ||
      this.onboardingComponentRef
    ) {
      return;
    }

    if (this.authService.currentUser?.onboarding_completed_at) {
      return;
    }

    if (this.hasCompletedOnboardingLocally()) {
      this.authService.markOnboardingCompleted().subscribe({
        error: () => undefined,
      });
      return;
    }

    const completedRemotely = await this.ensureFreshOnboardingState();
    if (
      this.isDestroyed ||
      this.onboardingComponentRef ||
      completedRemotely ||
      this.hasCompletedOnboarding()
    ) {
      return;
    }

    const { OnboardingWizardComponent } = await import(
      '@shared/onboarding/onboarding-wizard.component'
    );

    if (
      this.isDestroyed ||
      this.onboardingComponentRef ||
      this.hasCompletedOnboarding()
    ) {
      return;
    }

    const hostElement = this.document.createElement('app-onboarding-wizard-host');
    const componentRef = createComponent(OnboardingWizardComponent, {
      environmentInjector: this.environmentInjector,
      hostElement,
    });
    const closedSubscription = componentRef.instance.closed.subscribe(() => {
      this.markOnboardingCompletedLocally();
      this.destroyOnboarding();
    });

    componentRef.onDestroy(() => {
      closedSubscription.unsubscribe();
    });

    this.appRef.attachView(componentRef.hostView);
    this.document.body.appendChild(hostElement);
    this.onboardingComponentRef = componentRef;
    this.onboardingHostElement = hostElement;
  }

  private hasCompletedOnboarding(): boolean {
    return Boolean(this.authService.currentUser?.onboarding_completed_at) ||
      this.hasCompletedOnboardingLocally();
  }

  private ensureFreshOnboardingState(): Promise<boolean> {
    if (this.onboardingRemoteCheckInFlight) {
      return this.onboardingRemoteCheckInFlight;
    }

    const request = new Promise<boolean>((resolve) => {
      this.authService.refreshCurrentUser().subscribe({
        next: (user) => resolve(Boolean(user?.onboarding_completed_at)),
        error: () => resolve(this.hasCompletedOnboarding()),
      });
    });

    this.onboardingRemoteCheckInFlight = request;
    void request.finally(() => {
      if (this.onboardingRemoteCheckInFlight === request) {
        this.onboardingRemoteCheckInFlight = null;
      }
    });

    return request;
  }

  private hasCompletedOnboardingLocally(): boolean {
    const storageKeys = this.getOnboardingStorageKeys();
    if (!storageKeys.length) {
      return false;
    }

    try {
      const storage = this.document.defaultView?.localStorage;
      return storageKeys.some((storageKey) => storage?.getItem(storageKey) === 'true');
    } catch {
      return false;
    }
  }

  private markOnboardingCompletedLocally(): void {
    const storageKeys = this.getOnboardingStorageKeys();
    if (!storageKeys.length) {
      return;
    }

    try {
      const storage = this.document.defaultView?.localStorage;
      storageKeys.forEach((storageKey) => storage?.setItem(storageKey, 'true'));
    } catch {
      // El estado remoto sigue siendo la fuente principal; esta marca solo evita repetir el modal.
    }
  }

  private getOnboardingStorageKeys(): string[] {
    const currentUser = this.authService.currentUser;
    const userId = currentUser?.id;
    if (!userId) {
      return [];
    }

    const tenantId = currentUser.tenant_id ?? 'default';
    const keys = new Set([
      `ie-pos:onboarding-completed:${tenantId}:${userId}`,
      `ie-pos:onboarding-completed:default:${userId}`,
    ]);

    return [...keys];
  }

  private destroyOnboarding(): void {
    const componentRef = this.onboardingComponentRef;
    const hostElement = this.onboardingHostElement;

    this.onboardingComponentRef = null;
    this.onboardingHostElement = null;

    if (componentRef) {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    }

    hostElement?.remove();
  }
}
